import React, { useEffect, useRef, useState } from "react";
import { isYouTubeUrl, getYouTubeEmbedUrl } from "../utils/videoHelper";
import useInView from "../hooks/useInView";

const playBtnStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 56,
  height: 56,
  borderRadius: "50%",
  background: "rgba(0,0,0,0.55)",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 20,
  pointerEvents: "none",
};

const wrapperStyle = { position: "relative", width: "100%", height: "100%" };

/**
 * SmartVideoBlock — src chahe direct video file (mp4/webm) ho ya YouTube URL,
 * dono ko sahi tarike se render kar deta hai.
 *
 * Modes (mutually exclusive — jo bhi `true` set karo wahi chalega):
 *
 *  - playOnView (default false): koi click nahi chahiye. Video sirf tab
 *    autoplay hoti hai jab uska section scroll karke viewport mein aaye,
 *    aur viewport se bahar jaate hi pause ho jaati hai. Bahut saari
 *    videos ek page par hone par (category listing) yehi use karo.
 *
 *  - playOnInteraction (default false): video autoplay nahi hoti,
 *    user ke click/tap karne par play hoti hai.
 *
 *  - dono false ho to purana behaviour: `autoPlay` prop ke hisaab se
 *    mount hote hi autoplay (single/floating video jaisi jagah ke liye).
 *
 * lazy (default true): video/iframe tab tak DOM mein mount hi nahi hoti
 * jab tak wo pehli baar viewport ke paas na aaye (perf ke liye).
 */
function SmartVideoBlock({
  src,
  videoRef,
  className = "",
  autoPlay = true,
  muted = true,
  loop = true,
  controls = false,
  poster, 
  onLoadedData,
  playOnView = false,
  playOnInteraction = false,
  lazy = true,
}) {
  const [wrapperRef, inView] = useInView();
  const localVideoRef = useRef(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [ytActivated, setYtActivated] = useState(false);

  // Ek baar viewport ke paas aa gaya to element mount rakho (unmount/remount
  // baar baar karne se video restart/flicker hoti hai)
  useEffect(() => {
    if (inView) setHasLoadedOnce(true);
  }, [inView]);

  const shouldMount = !lazy || hasLoadedOnce;

  const setRefs = (el) => {
    localVideoRef.current = el;
    if (videoRef) {
      if (typeof videoRef === "function") videoRef(el);
      else videoRef.current = el;
    }
  };

  // playOnView mode: viewport mein aate hi play, bahar jaate hi pause
  useEffect(() => {
    if (!playOnView) return;
    const video = localVideoRef.current;
    if (!video) return;

    if (inView) {
      video.muted = muted;
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [inView, playOnView, muted, shouldMount]);

  if (!src) return null;

  const effectiveAutoPlay = playOnInteraction ? false : playOnView ? false : autoPlay;

  const handleWrapperClick = () => {
    if (!playOnInteraction) return;
    const video = localVideoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  if (isYouTubeUrl(src)) {
    // Click-to-play mode: click hone tak sirf poster + play button
    if (playOnInteraction && !ytActivated) {
      return (
        <div
          ref={wrapperRef}
          className={className}
          style={wrapperStyle}
          onClick={() => setYtActivated(true)}
          role="button"
          aria-label="Play video"
        >
          {shouldMount && poster && (
            <img src={poster} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          )}
          <span style={playBtnStyle}>▶</span>
        </div>
      );
    }

    if (!shouldMount) return <div ref={wrapperRef} className={className} style={wrapperStyle} />;

    // YouTube iframe ko JS se play/pause nahi kar sakte (postMessage API
    // ke bina), isliye playOnView ke liye ise sirf viewport ke paas
    // aane par (lazy) mount + autoplay kar dete hain.
    const embedUrl = getYouTubeEmbedUrl(src, {
      autoplay: playOnInteraction ? true : autoPlay,
      muted,
      loop,
      controls,
    });

    if (!embedUrl) return null;

    return (
      <iframe
        className={className}
        src={embedUrl}
        title="Product video"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return (
    <div ref={wrapperRef} className={className} style={wrapperStyle} onClick={handleWrapperClick}>
      {shouldMount && (
        <video
          ref={setRefs}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          loop={loop}
          muted={muted}
          playsInline
          autoPlay={effectiveAutoPlay}
          controls={controls}
          poster={poster}
          src={src}
          onLoadedData={onLoadedData}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
      )}
      {playOnInteraction && !isPlaying && <span style={playBtnStyle}>▶</span>}
    </div>
  );
}

export default SmartVideoBlock;
